import { apiRequest } from './api';
import { useSession } from '../contexts/SessionContext';
import { getLineChargeTotal, getLineListTotal, isLineItemCustomTotal } from './cartPricing';

/**
 * Utility functions for order creation and management.
 *
 * Zelle: frontend must NEVER call this for Zelle. Frontend only creates the pending
 * record (POST /api/zelle/generate-code) and polls DB for status (GET /api/zelle/order-status).
 * WC order creation, receipt email, and DB status update for Zelle are done only by the webhook.
 */

// Must reject whitespace: Stripe rejects addresses like "user@gmail .com" as receipt_email,
// which fails PaymentIntent creation and kills the payment. Mirrors RECEIPT_EMAIL_RE.
const ORDER_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const createWooCommerceOrder = async ({
  cartItems,
  customer,
  // Which physical kiosk this order came from; persisted on the order and passed to the queue.
  kioskId,
  // Preferred prop from older callers
  paymentMethod = undefined,
  // Also accept API-shaped fields from newer callers
  payment_method: payment_method_in,
  payment_method_title: payment_method_title_in,
  paymentStatus = 'completed',
  isPaid = true,
  // Optional overrides from FE calculations
  fee: feeOverride,
  total: totalOverride,
  // Zelle-only extra context (not used by kiosk; webhook completes Zelle)
  memo_code,
  sent_by,
  // Optional note override
  orderNotes,
  setSession = null, // Optional session setter
  isGuestCheckout = false,
  guestReceiptEmail,
}) => {
  const finalPaymentMethod = paymentMethod || payment_method_in || 'stripe_cc';
  if (finalPaymentMethod === 'zelle') {
    throw new Error(
      'Zelle orders are completed by the webhook only. Frontend must not call order creation for Zelle; use generate-code and poll order-status.'
    );
  }

  try {
    // Determine the customer ID, checking multiple possible locations
    const customerId = customer?.customer_id || customer?.id || process.env.REACT_APP_GUEST_CHECKOUT_USER_ID;
    console.log('🔵 [DEBUG] Creating order with customer ID:', {
      customerId,
      hasCustomer: !!customer,
      customerKeys: customer ? Object.keys(customer) : [],
      cartItemsCount: cartItems?.length || 0,
      hasPaymentMethod: !!(paymentMethod || payment_method_in)
    });

    // Validate required data
    if (!cartItems || !Array.isArray(cartItems) || cartItems.length === 0) {
      throw new Error('Cannot create order: cartItems is required and must be a non-empty array');
    }

    // Resolve payment title from any of the supported inputs (finalPaymentMethod set above)
    const finalPaymentTitle = payment_method_title_in
      || (finalPaymentMethod === 'zelle' ? 'Zelle'
        : finalPaymentMethod === 'swipe' ? 'Swipe'
        : (process.env.REACT_APP_WC_PAYMENT_METHOD_TITLE || 'Credit Card'));

    const orderData = {
      status: paymentStatus,
      currency: process.env.REACT_APP_WC_PAYMENT_CURRENCY || 'usd',
      customer_id: customerId,
      ...(Number.isFinite(parseInt(kioskId, 10)) ? { kiosk_id: parseInt(kioskId, 10) } : {}),
      payment_method: finalPaymentMethod,
      payment_method_title: finalPaymentTitle,
      set_paid: isPaid,
      line_items: cartItems.map((item) => {
        const qty = item.quantity || 1;
        const base = {
          product_id: item.product_id,
          quantity: qty,
          variation_id: item.variation_id || 0,
          list_line_total: getLineListTotal(item).toFixed(2)
        };
        if (isLineItemCustomTotal(item)) {
          const t = getLineChargeTotal(item).toFixed(2);
          base.subtotal = t;
          base.total = t;
        }
        return base;
      }),
      customer_note: process.env.REACT_APP_WC_PAYMENT_CUSTOMER_NOTE || 'Kiosk order',
      meta_data: [
        {
          key: '_kiosk_order',
          value: 'yes'
        },
        {
          key: '_order_source',
          value: 'kiosk'
        }
      ]
    };

    if (orderNotes && typeof orderNotes === 'string') {
      orderData.customer_note = orderNotes;
    }

    // Add billing details if available; do NOT send an invalid/empty email
    const billing = {
      first_name: customer?.first_name || customer?.billing?.first_name || undefined,
      last_name: customer?.last_name || customer?.billing?.last_name || undefined,
      phone: customer?.phone || customer?.billing?.phone || undefined,
      address_1: customer?.billing?.address_1 || undefined,
      city: customer?.billing?.city || undefined,
      state: customer?.billing?.state || undefined,
      postcode: customer?.billing?.postcode || undefined,
      country: customer?.billing?.country || 'US'
    };
    const isGuestOrder = isGuestCheckout === true || customer?.isGuestCheckout === true;
    if (isGuestOrder) {
      const receiptEmail = guestReceiptEmail || customer?.guestReceiptEmail;
      if (typeof receiptEmail === 'string' && ORDER_EMAIL_RE.test(receiptEmail.trim())) {
        const placeholder = (process.env.REACT_APP_GUEST_CHECKOUT_EMAIL || '').trim().toLowerCase();
        const trimmed = receiptEmail.trim();
        if (!placeholder || trimmed.toLowerCase() !== placeholder) {
          billing.email = trimmed;
        }
      }
    } else {
      const emailCandidate = customer?.email || customer?.billing?.email || '';
      const emailValid = typeof emailCandidate === 'string' && ORDER_EMAIL_RE.test(emailCandidate.trim());
      if (emailValid) {
        billing.email = emailCandidate;
      }
    }
    // Only attach billing if at least one useful field is present
    const billingKeys = Object.keys(billing).filter(k => billing[k] != null && billing[k] !== '');
    if (billingKeys.length > 0) {
      orderData.billing = billing;
    }

    // Compute subtotal and fees, honoring overrides from FE
    const feePercent = parseFloat(process.env.REACT_APP_CREDIT_CARD_FEE_PERCENTAGE || '2.5');
    const flatFeeRaw = parseFloat(process.env.REACT_APP_FLAT_FEE || '1');
    const flatFee = Number.isFinite(flatFeeRaw) ? flatFeeRaw : 1;

    const subtotal = cartItems.reduce((sum, item) => sum + getLineChargeTotal(item), 0);

    // Determine fee: prefer override; else apply rules for card/swipe (Zelle is rejected above)
    let feeNumber = 0;
    {
      const computedFee = subtotal <= 10 ? flatFee : (subtotal * (feePercent / 100));
      feeNumber = (typeof feeOverride === 'number') ? feeOverride : parseFloat(computedFee.toFixed(2));
      orderData.fee_lines = [{
        name: process.env.REACT_APP_CREDIT_CARD_FEE_NAME || 'Credit Card Processing Fee',
        total: feeNumber.toFixed(2)
      }];
    }

    // Determine total: prefer override; else subtotal + fee (fee may be 0 for Zelle)
    const computedTotal = parseFloat((subtotal + (feeNumber || 0)).toFixed(2));
    const totalNumber = (typeof totalOverride === 'number')
      ? parseFloat(totalOverride.toFixed(2))
      : computedTotal;
    orderData.total = totalNumber.toFixed(2);

    // Validate orderData before sending
    if (!orderData.customer_id && !orderData.billing?.email) {
      console.error('❌ [Order Utils] Order data validation failed:', {
        hasCustomerId: !!orderData.customer_id,
        hasBillingEmail: !!orderData.billing?.email,
        orderDataKeys: Object.keys(orderData)
      });
      throw new Error('Order must have either customer_id or billing.email');
    }

    if (!orderData.line_items || orderData.line_items.length === 0) {
      console.error('❌ [Order Utils] Order data validation failed: no line items');
      throw new Error('Order must have at least one line item');
    }

    console.log('Creating WooCommerce order with data:', JSON.stringify({
      customer_id: orderData.customer_id,
      line_items_count: orderData.line_items?.length,
      payment_method: orderData.payment_method,
      total: orderData.total,
      has_billing: !!orderData.billing
    }, null, 2));

    // Build a deterministic idempotency key from stable order fields
    const stableForKey = JSON.stringify({
      customer_id: orderData.customer_id,
      items: orderData.line_items.map((i) => ({
        p: i.product_id,
        v: i.variation_id || 0,
        q: i.quantity || 1,
        t: i.total != null ? i.total : null
      })),
      total: orderData.total,
      pm: orderData.payment_method,
      note: orderData.customer_note,
      meta: (orderData.meta_data || []).filter(m => ['memo_code', 'sent_by', '_kiosk_order', '_order_source'].includes(m.key))
    });
    let hash = 0;
    for (let i = 0; i < stableForKey.length; i++) {
      hash = ((hash << 5) - hash) + stableForKey.charCodeAt(i);
      hash |= 0; // Convert to 32bit integer
    }
    const idemKey = `kiosk-${Math.abs(hash)}`;
    
    // Use the apiRequest utility which will use the REACT_APP_API_BASE_URL
    console.log('📤 [Order Utils] Sending order request to /api/orders');
    console.log('📤 [Order Utils] OrderData to send:', JSON.stringify(orderData, null, 2));
    console.log('📤 [Order Utils] Stringified body length:', JSON.stringify(orderData).length);
    
    const order = await apiRequest('/api/orders', {
      method: 'POST',
      headers: {
        'Idempotency-Key': idemKey
      },
      body: JSON.stringify(orderData)
    });
    
    console.log('✅ [Order Utils] Order created successfully:', order);

    // Store the complete order in the session if setSession is provided
    if (setSession && order.id) {
      const sessionUpdate = {
        currentOrder: order,
        currentOrderId: order.id,
        orderStatus: order.status || 'completed',
        orderDate: new Date().toISOString(),
        orderDetails: {
          id: order.id,
          number: order.number || order.id,
          status: order.status || 'completed',
          date_created: order.date_created || new Date().toISOString(),
          total: order.total || orderData.total,
          payment_method: order.payment_method || paymentMethod,
          payment_method_title: order.payment_method_title || orderData.payment_method_title,
          line_items: order.line_items || cartItems,
          billing: order.billing || orderData.billing,
          shipping: order.shipping || {},
          fee_lines: order.fee_lines || orderData.fee_lines,
          customer_id: order.customer_id || customer?.customer_id,
          customer_note: order.customer_note || orderData.customer_note,
          meta_data: order.meta_data || orderData.meta_data
        },
        customer: {
          ...customer,
          customer_id: customer?.customer_id || process.env.REACT_APP_GUEST_CHECKOUT_USER_ID
        }
      };

      setSession(prev => ({
        ...prev,
        ...sessionUpdate
      }));
      
      console.log('[Order Utils] Order stored in session:', sessionUpdate);
    }
    
    return order;
    
  } catch (error) {
    console.error('Order creation error:', error);
    throw new Error(error.message || 'Failed to create order. Please try again.');
  }
};

/**
 * CC/Swipe: on payment success – create WC order and send receipt (backend creates order with status completed).
 * @param {number|string} kioskCcOrderId - ID from POST /api/orders (CC) response
 * @param {Object} opts - { payment_intent_id }
 * @returns {Promise<Object>} { id (first WC *payment* order), order_key, status, total, email_sent, pledge_order_ids?, payment_order_ids? }
 */
export const completeCcOrder = async (kioskCcOrderId, opts = {}) => {
  if (!kioskCcOrderId) throw new Error('kiosk_cc_order_id is required');
  const response = await apiRequest(`/api/orders/cc/${kioskCcOrderId}/complete`, {
    method: 'POST',
    body: JSON.stringify({ payment_intent_id: opts.payment_intent_id || null })
  });
  return response;
};

/**
 * CC/Swipe: on payment failure – mark local order failed (no WC order created).
 * @param {number|string} kioskCcOrderId - ID from POST /api/orders (CC) response
 * @param {Object} opts - { error_message }
 */
export const failCcOrder = async (kioskCcOrderId, opts = {}) => {
  if (!kioskCcOrderId) throw new Error('kiosk_cc_order_id is required');
  await apiRequest(`/api/orders/cc/${kioskCcOrderId}/fail`, {
    method: 'PUT',
    body: JSON.stringify({ error_message: opts.error_message || 'Payment failed' })
  });
};

/**
 * CC/Swipe: on user cancel/abort – mark local order cancelled (no WC order created).
 * @param {number|string} kioskCcOrderId - ID from POST /api/orders (CC) response
 * @param {Object} opts - { error_message }
 */
export const cancelCcOrder = async (kioskCcOrderId, opts = {}) => {
  if (!kioskCcOrderId) throw new Error('kiosk_cc_order_id is required');
  await apiRequest(`/api/orders/cc/${kioskCcOrderId}/cancel`, {
    method: 'PUT',
    body: JSON.stringify({ error_message: opts.error_message || 'Payment canceled by user' })
  });
};

